// LiveWhiteboard — Pizarra colaborativa en tiempo real basada en Excalidraw.
// Sincroniza vía WebSocket /api/ws/whiteboard/{classId}. El backend persiste
// el snapshot completo en MongoDB (last-writer-wins) y rebota a los demás
// participantes. El moderador (docente) puede limpiar la pizarra.
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Excalidraw, exportToBlob, convertToExcalidrawElements } from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";
import "./LiveWhiteboard.css";
import jsPDF from "jspdf";
import { Eraser, Wifi, WifiOff, Users, FileDown, Highlighter, Sigma, ImageUp, Loader2, Table as TableIcon } from "lucide-react";
import { api, getAccessToken } from "@/lib/api";
import { MathFormulaModal } from "./MathFormulaEditor";
import { texToPng } from "@/lib/texToImage";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Construye la URL WS a partir del backend HTTP(S).
const WS_BASE = BACKEND_URL.replace(/^http/, "ws");

const LiveWhiteboard = forwardRef(function LiveWhiteboard({ classId, canModerate, userName = "", onRemoteShare }, ref) {
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);
  const [status, setStatus] = useState("connecting"); // connecting|online|offline
  const [peers, setPeers] = useState(0);
  const [lastEditor, setLastEditor] = useState("");      // autor de la última edición (sutil)
  const [studentCanDraw, setStudentCanDraw] = useState(true); // permiso de dibujo del estudiante
  const insertOffsetRef = useRef(0);                      // evita apilar inserciones
  const highlighterModeRef = useRef(false);              // C51: modo resaltador activo
  const wsRef = useRef(null);
  const apiRef = useRef(null);              // ref a excalidrawAPI para usar dentro del handler WS
  const pendingInitRef = useRef(null);      // snapshot llegado antes de tener API listo
  const lastRemoteVersionRef = useRef(0);
  const lastSentVersionRef = useRef(0);
  const lastAppliedAtRef = useRef(null);    // dedupe del polling REST (updated_at)
  const lastLocalEditAtRef = useRef(0);     // marca de la última edición local (anti-clobber)
  const broadcastTimerRef = useRef(null);
  // Token JWT desde el helper central (clave `neuromath_access_token`).
  const tokenRef = useRef(getAccessToken());

  // Fix 2.1.3: isMounted gate — Excalidraw solo se renderiza cuando el
  // componente está montado en el DOM, evitando que cree nodos internos
  // (canvas/SVG) que React luego no puede removeChild bajo StrictMode.
  const [excalidrawReady, setExcalidrawReady] = useState(false);
  useEffect(() => {
    setExcalidrawReady(true);
    return () => {
      // Limpiar timers pendientes al desmontar
      if (broadcastTimerRef.current) clearTimeout(broadcastTimerRef.current);
    };
  }, []);

  // C62 — Cursores en vivo (presence). C53 — Protección de trazo por elemento.
  const collaboratorsRef = useRef(new Map());   // id_usuario → {username, pointer, color}
  const presenceThrottleRef = useRef(0);
  const myColorRef = useRef("");
  const remoteIdsRef = useRef(new Set());       // ids vistos en snapshots remotos
  const myIdsRef = useRef(new Set());           // ids creados por MÍ
  const protectedElsRef = useRef(new Map());    // id → elemento (para restaurar)
  const restoringRef = useRef(false);
  const applyingRemoteRef = useRef(false);   // evita aviso falso al aplicar snapshot remoto
  const protectToastRef = useRef(0);

  const CURSOR_COLORS = ["#A43790", "#2C49A0", "#3FA84D", "#E8590C", "#7048E8", "#0CA678"];
  const colorForName = (name) => {
    let h = 0; for (const c of String(name || "u")) h = (h * 31 + c.charCodeAt(0)) | 0;
    return CURSOR_COLORS[Math.abs(h) % CURSOR_COLORS.length];
  };
  const applyCollaborators = () => {
    const api = apiRef.current;
    if (api) api.updateScene({ appState: { collaborators: new Map(collaboratorsRef.current), isCollaborating: true } });
  };

  // Cuando el API de Excalidraw está disponible, aplica cualquier snapshot
  // que haya llegado por WS antes de que el componente terminara de montar.
  useEffect(() => {
    apiRef.current = excalidrawAPI;
    if (excalidrawAPI && pendingInitRef.current !== null) {
      applyRemoteSnapshot(pendingInitRef.current);
      pendingInitRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excalidrawAPI]);

  // ------------------------------------------------------------------
  // Conexión WebSocket
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!classId || !tokenRef.current) return;
    let cancelled = false;
    const url = `${WS_BASE}/api/ws/whiteboard/${classId}?token=${encodeURIComponent(tokenRef.current)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    setStatus("connecting");

    ws.onopen = () => {
      if (cancelled) return;
      setStatus("online");
    };
    ws.onclose = () => {
      if (!cancelled) setStatus("offline");
    };
    ws.onerror = () => {
      if (!cancelled) setStatus("offline");
    };
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "init" || msg.type === "snapshot") {
        const snap = msg.snapshot ?? msg.data;
        if (msg.author) setLastEditor(msg.author);
        if (apiRef.current) {
          applyRemoteSnapshot(snap);
        } else {
          // El componente Excalidraw aún no terminó de inicializar.
          pendingInitRef.current = snap;
        }
      } else if (msg.type === "leave") {
        if (msg.from) { collaboratorsRef.current.delete(msg.from); applyCollaborators(); }
        setPeers((p) => Math.max(0, p - 1));
      } else if (msg.type === "control") {
        // Control del moderador: compartir pizarra o permiso de dibujo.
        if (msg.action === "share_board" && typeof onRemoteShare === "function") {
          onRemoteShare(!!(msg.data && msg.data.shared));
        } else if (msg.action === "student_draw") {
          setStudentCanDraw(!!(msg.data && msg.data.allowed));
        }
      } else if (msg.type === "presence") {
        // C62 — cursor remoto con nombre. Actualiza el colaborador y re-pinta.
        const d = msg.data || {};
        if (msg.from && typeof d.x === "number" && typeof d.y === "number") {
          const col = d.color || "#A43790";
          collaboratorsRef.current.set(msg.from, {
            username: msg.from_name || d.name || "Usuario",
            pointer: { x: d.x, y: d.y },
            button: d.button === "down" ? "down" : "up",
            color: { background: col, stroke: col },
          });
          applyCollaborators();
        }
      }
    };

    // Ping keep-alive cada 25s para evitar idle timeouts del proxy.
    const pingTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
      }
    }, 25_000);

    return () => {
      cancelled = true;
      clearInterval(pingTimer);
      if (broadcastTimerRef.current) clearTimeout(broadcastTimerRef.current);
      try { ws.close(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  // ------------------------------------------------------------------
  // Devuelve SOLO los archivos (imágenes, p. ej. fórmulas) referenciados por
  // los elementos actuales, para incluirlos en el snapshot y que los demás
  // participantes los vean. Si no hay imágenes, devuelve undefined (sin overhead).
  const filesForElements = (elements) => {
    const apiObj = apiRef.current;
    if (!apiObj?.getFiles) return undefined;
    const ids = new Set();
    for (const e of elements || []) {
      if (e && e.type === "image" && !e.isDeleted && e.fileId) ids.add(e.fileId);
    }
    if (!ids.size) return undefined;
    const all = apiObj.getFiles() || {};
    const needed = {};
    for (const id of ids) if (all[id]) needed[id] = all[id];
    return Object.keys(needed).length ? needed : undefined;
  };

  // Aplicar snapshot remoto sin entrar en loop con onChange local.
  // ------------------------------------------------------------------
  const applyRemoteSnapshot = (snapshot) => {
    const api = apiRef.current;
    if (!api) return;
    if (!snapshot) {
      // Pizarra borrada
      api.updateScene({ elements: [], appState: { collaborators: new Map(collaboratorsRef.current) } });
      lastRemoteVersionRef.current = 0;
      remoteIdsRef.current = new Set();
      protectedElsRef.current = new Map();
      return;
    }
    try {
      const els = snapshot.elements || [];
      // Si el snapshot que llega no incluye un elemento que YO acabo de crear
      // (porque mi cambio aún no le llegó a quien lo mandó), consérvalo en vez
      // de dejar que este snapshot más viejo lo borre.
      // v5.6.0 · MEZCLA por versión (en vez de reemplazar toda la escena): si mi copia local de un elemento es más
      // nueva que la que llega (p. ej. el trazo que estoy dibujando en este momento), se conserva la mía. Antes un
      // snapshot ajeno borraba lo recién escrito y la escritura parecía «intermitente».
      const incomingIds = new Set(els.filter((e) => e && e.id).map((e) => e.id));
      const currentLocalElements = api.getSceneElementsIncludingDeleted
        ? api.getSceneElementsIncludingDeleted()
        : (api.getSceneElements ? api.getSceneElements() : []);
      const localById = new Map(currentLocalElements.filter((e) => e && e.id).map((e) => [e.id, e]));
      const merged = els.map((e) => {
        const loc = e && e.id ? localById.get(e.id) : null;
        return loc && (loc.version || 0) > (e.version || 0) ? loc : e;
      });
      const preserveMine = currentLocalElements.filter(
        (e) => e && !e.isDeleted && myIdsRef.current.has(e.id) && !incomingIds.has(e.id)
      );
      const finalElements = preserveMine.length ? [...merged, ...preserveMine] : merged;
      // C53 — reconstruir ids/elementos remotos desde ESTE snapshot (así, si el
      // docente borró algo, deja de estar protegido y no se "restaura" en falso).
      const freshIds = new Set();
      const freshEls = new Map();
      for (const el of els) {
        if (!el || !el.id) continue;
        freshIds.add(el.id);
        freshEls.set(el.id, el);
      }
      remoteIdsRef.current = freshIds;
      protectedElsRef.current = freshEls;
      applyingRemoteRef.current = true; // evita que el onChange resultante dispare el aviso
      // Registrar los archivos (imágenes/fórmulas) que traiga el snapshot ANTES
      // de actualizar la escena, para que los elementos image se rendericen.
      if (snapshot.files && typeof snapshot.files === "object") {
        try {
          const arr = Object.values(snapshot.files).filter((f) => f && f.id && f.dataURL);
          if (arr.length) api.addFiles(arr);
        } catch { /* addFiles no disponible */ }
      }
      api.updateScene({
        elements: finalElements,
        appState: { ...(snapshot.appState || {}), collaborators: new Map(collaboratorsRef.current) },
      });
      setTimeout(() => { applyingRemoteRef.current = false; }, 150);
      // Forzamos a que la próxima onChange con esta misma versión no se reenvíe.
      lastRemoteVersionRef.current = computeVersion(snapshot.elements || []);
      lastSentVersionRef.current = lastRemoteVersionRef.current;
    } catch (e) {
      console.warn("Snapshot inválido, ignorado", e);
    }
  };

  // Heurística simple: suma el "version" de cada elemento que expone Excalidraw.
  const computeVersion = (elements) => {
    let v = 0;
    for (const el of elements || []) {
      v += (el.version || 0) | 0;
    }
    return v;
  };

  // ------------------------------------------------------------------
  // onChange local → debounced broadcast.
  // ------------------------------------------------------------------
  const onChange = (elements, appState) => {
    // v5.6.0 · TODOS los roles (no solo el estudiante) registran qué elementos creó esta persona, para que un
    // snapshot ajeno que llegue a mitad del trazo no los borre.
    if (!restoringRef.current && !applyingRemoteRef.current) {
      for (const e of elements) {
        if (!e.isDeleted && !remoteIdsRef.current.has(e.id) && !myIdsRef.current.has(e.id)) myIdsRef.current.add(e.id);
      }
    }
    // C53 — Protección de trazo: el ESTUDIANTE no puede BORRAR lo que dibujó el
    // docente. Los elementos remotos (no creados por mí) quedan protegidos; si
    // el alumno los elimina, los restauramos y no difundimos el borrado.
    if (!canModerate && !restoringRef.current && !applyingRemoteRef.current) {
      const currentIds = new Set(elements.filter((e) => !e.isDeleted).map((e) => e.id));
      for (const e of elements) {
        if (!e.isDeleted && !remoteIdsRef.current.has(e.id) && !myIdsRef.current.has(e.id)) {
          myIdsRef.current.add(e.id); // elemento creado por MÍ
        }
      }
      const missing = [];
      for (const id of remoteIdsRef.current) {
        if (myIdsRef.current.has(id)) continue;
        if (!currentIds.has(id) && protectedElsRef.current.has(id)) {
          missing.push(protectedElsRef.current.get(id));
        }
      }
      if (missing.length) {
        restoringRef.current = true;
        const kept = elements.filter((e) => !e.isDeleted);
        const restored = [...kept, ...missing.filter((m) => !currentIds.has(m.id))];
        apiRef.current?.updateScene({ elements: restored });
        // Bug 2.4: Toast solo UNA vez cada 30s (no loop continuo con eraser activo)
        if (Date.now() - protectToastRef.current > 30000) {
          protectToastRef.current = Date.now();
        }
        setTimeout(() => { restoringRef.current = false; }, 60);
        return; // no difundimos el borrado del trazo del docente
      }
    }

    // C51: el resaltador fija estilos globales (borde transparente, opacidad
    // baja, relleno amarillo). Si el docente cambia de herramienta después de
    // resaltar, restauramos los estilos por defecto para que el círculo, el
    // cuadrado o el texto NO hereden la apariencia de resaltador (ni el texto
    // quede invisible por borde transparente).
    if (highlighterModeRef.current && appState?.activeTool?.type && appState.activeTool.type !== "rectangle") {
      highlighterModeRef.current = false;
      const api = apiRef.current;
      if (api) {
        api.updateScene({
          appState: {
            currentItemStrokeColor: "#1e1e1e",
            currentItemBackgroundColor: "transparent",
            currentItemFillStyle: "solid",
            currentItemOpacity: 100,
            currentItemRoughness: 1,
          },
        });
      }
    }
    const v = computeVersion(elements);
    if (v === lastSentVersionRef.current) return;
    lastSentVersionRef.current = v;
    lastLocalEditAtRef.current = Date.now(); // marca edición local (anti-clobber)
    if (broadcastTimerRef.current) clearTimeout(broadcastTimerRef.current);
    broadcastTimerRef.current = setTimeout(() => {
      // appState mínimo para no inflar el payload (background, theme, viewBg)
      const files = filesForElements(elements);
      const snap = {
        elements,
        appState: {
          viewBackgroundColor: "#ffffff",
        },
        ...(files ? { files } : {}),
      };
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: "snapshot", data: snap, author: userName }));
        } catch {}
      } else {
        // Fallback REST cuando el WebSocket no está disponible (p. ej. proxy
        // de producción bloquea WSS). Persiste el snapshot para los pollers.
        api.post(`/live-class/${classId}/whiteboard/state`, { snapshot: snap, has_snapshot: true, author: userName }).catch(() => {});
      }
    }, 350);
  };

  // Envío inmediato (para inserciones programáticas: fórmula / OCR).
  // C62 — Enviar mi cursor (puntero) a los demás por WS (throttle ~70ms).
  const handlePointerUpdate = (payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const p = payload && payload.pointer;
    if (!p) return;
    const now = Date.now();
    if (now - presenceThrottleRef.current < 70) return;
    presenceThrottleRef.current = now;
    if (!myColorRef.current) myColorRef.current = colorForName(userName);
    try {
      ws.send(JSON.stringify({
        type: "presence",
        data: { x: p.x, y: p.y, button: payload.button, name: userName || "Usuario", color: myColorRef.current },
      }));
    } catch { /* ws no disponible */ }
  };


  const sendSnapshotNow = (elements) => {
    lastSentVersionRef.current = computeVersion(elements);
    lastLocalEditAtRef.current = Date.now(); // protege inserciones programáticas del clobber por polling
    const files = filesForElements(elements);
    const snap = { elements, appState: { viewBackgroundColor: "#ffffff" }, ...(files ? { files } : {}) };
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "snapshot", data: snap, author: userName }));
      } catch {}
    } else {
      api.post(`/live-class/${classId}/whiteboard/state`, { snapshot: snap, has_snapshot: true, author: userName }).catch(() => {});
    }
  };

  // Envía un mensaje de control (docente): compartir pizarra / permiso. Se
  // envía por WS y ADEMÁS se persiste vía REST para que los estudiantes que
  // sincronizan por polling (sin WS) reciban el cambio.
  const sendControl = (action, data) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: "control", action, data })); } catch {}
    }
    if (action === "share_board") {
      api.post(`/live-class/${classId}/whiteboard/state`, { shared: !!(data && data.shared) }).catch(() => {});
    } else if (action === "student_draw") {
      api.post(`/live-class/${classId}/whiteboard/state`, { student_can_draw: !!(data && data.allowed) }).catch(() => {});
    }
  };

  // Polling de respaldo (solo estudiante): sincroniza snapshot + flags cada 5 s
  // por si el WebSocket está caído/bloqueado. El docente NO aplica polled
  // snapshots para no interrumpir su dibujo en curso.
  useEffect(() => {
    if (!classId || canModerate) return;
    let stop = false;
    const tick = async () => {
      if (Date.now() - lastLocalEditAtRef.current < 3000) return;
      try {
        const { data } = await api.get(`/live-class/${classId}/whiteboard/state`);
        if (stop) return;
        // Si por algún motivo el snapshot no trae autor (p. ej. dato antiguo),
        // lo tratamos como ajeno y lo aplicamos: el guard de edición local
        // reciente (3 s) evita pisar el trazo propio en curso.
        const isOther = data.author ? data.author !== userName : true;
        if (data.updated_at && data.updated_at !== lastAppliedAtRef.current && isOther) {
          lastAppliedAtRef.current = data.updated_at;
          if (data.author) setLastEditor(data.author);
          applyRemoteSnapshot(data.snapshot);
        }
        if (typeof onRemoteShare === "function") onRemoteShare(!!data.shared);
        setStudentCanDraw(data.student_can_draw !== false);
      } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, canModerate]);

  // Polling de respaldo (solo DOCENTE): recibe lo que dibuja el ESTUDIANTE aun
  // cuando el WebSocket está bloqueado en producción → pizarra BIDIRECCIONAL
  // automática, sin que nadie active nada manual. Para no pisar el trazo en
  // curso del docente, NO aplicamos un snapshot remoto si el docente dibujó
  // hace menos de 3 s, y solo aplicamos ediciones de OTRO autor.
  useEffect(() => {
    if (!classId || !canModerate) return;
    let stop = false;
    const tick = async () => {
      if (Date.now() - lastLocalEditAtRef.current < 3000) return;
      try {
        const { data } = await api.get(`/live-class/${classId}/whiteboard/state`);
        if (stop) return;
        // Si por algún motivo el snapshot no trae autor (p. ej. dato antiguo),
        // lo tratamos como ajeno y lo aplicamos: el guard de edición local
        // reciente (3 s) evita pisar el trazo propio en curso.
        const isOther = data.author ? data.author !== userName : true;
        if (data.updated_at && data.updated_at !== lastAppliedAtRef.current && isOther) {
          lastAppliedAtRef.current = data.updated_at;
          setLastEditor(data.author);
          applyRemoteSnapshot(data.snapshot);
        }
      } catch { /* ignore */ }
    };
    const iv = setInterval(tick, 4000);
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, canModerate, userName]);

  const insertText = (text, { fontSize = 20 } = {}) => {
    const api = apiRef.current;
    if (!api || !text) return;
    const off = (insertOffsetRef.current = (insertOffsetRef.current + 1) % 12);
    const st = api.getAppState?.() || {};
    const zoom = st.zoom?.value || 1;
    const baseX = (st.width || 600) / 2 / zoom - (st.scrollX || 0) - 80;
    const baseY = (st.height || 400) / 3 / zoom - (st.scrollY || 0) + off * 8;
    const skeleton = [{
      type: "text",
      x: baseX,
      y: baseY,
      text: String(text),
      fontSize,
      strokeColor: "#1F2547",
      customData: { author: userName || "Docente" },
    }];
    let newEls = [];
    try { newEls = convertToExcalidrawElements(skeleton); } catch { return; }
    const cur = api.getSceneElements();
    const all = [...cur, ...newEls];
    api.updateScene({ elements: all });
    try { api.scrollToContent(newEls, { fitToContent: false, animate: true }); } catch {}
    sendSnapshotNow(all);
    setLastEditor(userName || "Docente");
  };

  // ---- Editor de fórmulas (clon del CAD: MathFormulaModal, KaTeX/LaTeX) ----
  const [showMathEditor, setShowMathEditor] = useState(false);
  const [insertingFormula, setInsertingFormula] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);

  // Inserta una fórmula LaTeX como OBJETO (imagen nítida) en la pizarra.
  // Renderiza con MathJax → SVG vectorial → PNG de alta resolución → elemento
  // `image` de Excalidraw. Se sincroniza como parte del snapshot (incluye files).
  const insertFormula = async (latex) => {
    const apiObj = apiRef.current;
    if (!apiObj || !latex || !latex.trim()) return;
    setInsertingFormula(true);
    let dataURL, w, h;
    try {
      const out = await texToPng(latex, { color: "#1F2547", scale: 3 });
      dataURL = out.dataURL;
      w = out.width;
      h = out.height;
    } catch (e) {
      setInsertingFormula(false);
      toast.error("No se pudo renderizar la fórmula (LaTeX inválido)");
      return;
    }
    const fileId = `formula-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      apiObj.addFiles([{ id: fileId, mimeType: "image/png", dataURL, created: Date.now() }]);
    } catch { /* addFiles no disponible */ }
    const st = apiObj.getAppState?.() || {};
    const zoom = st.zoom?.value || 1;
    const x = (st.width || 600) / 2 / zoom - (st.scrollX || 0) - w / 2;
    const y = (st.height || 400) / 3 / zoom - (st.scrollY || 0);
    const skeleton = [{ type: "image", fileId, x, y, width: w, height: h, status: "saved", customData: { author: userName || "Docente", formulaLatex: latex } }];
    let newEls = [];
    try { newEls = convertToExcalidrawElements(skeleton); } catch { setInsertingFormula(false); toast.error("No se pudo insertar la fórmula"); return; }
    // Aseguramos que la imagen se renderice de inmediato (no quede 'pending').
    newEls = newEls.map((e) => (e.type === "image" ? { ...e, fileId, status: "saved" } : e));
    const cur = apiObj.getSceneElements();
    const all = [...cur, ...newEls];
    apiObj.updateScene({ elements: all });
    try { apiObj.scrollToContent(newEls, { fitToContent: false, animate: true }); } catch {}
    sendSnapshotNow(all);
    setLastEditor(userName || "Docente");
    setInsertingFormula(false);
    setShowMathEditor(false);
    toast.success("Fórmula insertada en la pizarra");
  };


  // ---- Foto manuscrita → OCR (Gemini) → texto en la pizarra ----
  const insertTable = () => {
    const api = apiRef.current;
    if (!api) return;
    const cellW = 120, cellH = 50;
    const startX = 100, startY = 100;
    const skeletons = [];
    // Líneas horizontales
    for (let r = 0; r <= tableRows; r++) {
      skeletons.push({
        type: "line", x: startX, y: startY + r * cellH,
        points: [[0, 0], [tableCols * cellW, 0]],
        strokeColor: "#1F2547", strokeWidth: 2,
      });
    }
    // Líneas verticales
    for (let c = 0; c <= tableCols; c++) {
      skeletons.push({
        type: "line", x: startX + c * cellW, y: startY,
        points: [[0, 0], [0, tableRows * cellH]],
        strokeColor: "#1F2547", strokeWidth: 2,
      });
    }
    const newEls = convertToExcalidrawElements(skeletons);
    const current = api.getSceneElements();
    api.updateScene({ elements: [...current, ...newEls] });
    setShowTableModal(false);
  };

  // ---- Foto manuscrita (original) ----
  const ocrInputRef = useRef(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const onOcrFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("La imagen supera 8 MB"); return; }
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/ocr", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const text = (data?.text || "").trim();
      if (!text || text === "[sin texto legible]") {
        toast.warning("No se reconoció texto legible en la imagen");
      } else {
        insertText(text, { fontSize: 20 });
        toast.success("Texto reconocido y colocado en la pizarra");
      }
    } catch (err) {
      toast.error("No se pudo reconocer la imagen: " + (err?.response?.data?.detail || "error"));
    } finally {
      setOcrLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Limpiar pizarra (sólo docente / admin).
  // ------------------------------------------------------------------
  const clearBoard = async () => {
    if (!canModerate) return;
    if (!window.confirm("¿Borrar todo el contenido de la pizarra? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/whiteboards/${classId}`);
      toast.success("Pizarra limpiada");
      applyRemoteSnapshot(null);
    } catch (e) {
      toast.error("No se pudo limpiar la pizarra");
    }
  };

  // Resaltador: activa la herramienta rectángulo con relleno amarillo
  // translúcido y sin borde, para "subrayar" zonas como con un marcador (#1).
  const useHighlighter = () => {
    const api = apiRef.current;
    if (!api) return;
    highlighterModeRef.current = true;
    api.updateScene({
      appState: {
        currentItemBackgroundColor: "#ffd43b",
        currentItemFillStyle: "solid",
        currentItemStrokeColor: "transparent",
        currentItemOpacity: 35,
        currentItemRoughness: 0,
      },
    });
    api.setActiveTool({ type: "rectangle" });
  };

  // ------------------------------------------------------------------
  // Exportar pizarrón a PDF (descarga local + subir al backend para
  // vincularlo a la grabación más reciente de la clase).
  // ------------------------------------------------------------------
  const [exportingPdf, setExportingPdf] = useState(false);
  // exportPdf({ recordingId, silent }) — `silent` (auto al detener grabación)
  // evita la descarga local y usa toasts discretos. Sin args = export manual.
  const exportPdf = async (opts = {}) => {
    const recordingId = opts && typeof opts === "object" ? opts.recordingId : undefined;
    const silent = !!(opts && typeof opts === "object" && opts.silent);
    const apiRef2 = apiRef.current;
    if (!apiRef2) {
      if (!silent) toast.error("Pizarrón no listo");
      return;
    }
    setExportingPdf(true);
    try {
      const elements = apiRef2.getSceneElements();
      const appState = apiRef2.getAppState();
      if (!elements || elements.length === 0) {
        if (!silent) toast.warning("La pizarra está vacía");
        setExportingPdf(false);
        return;
      }

      // Bug 2.7: Asegurar que TODOS los files (imágenes/fórmulas) estén
      // registrados y resueltos en getFiles() antes de llamar exportToBlob.
      const allFiles = apiRef2.getFiles?.() || {};

      // Bug 2.8: Scale dinámico — si el canvas es muy grande, reducimos scale
      // para no superar el límite del navegador (~16384px por dimensión).
      const MAX_CANVAS_SIDE = 16384;
      const computeScale = (w, h) => {
        let s = 2; // ideal
        while (s > 0.5 && (w * s > MAX_CANVAS_SIDE || h * s > MAX_CANVAS_SIDE)) {
          s *= 0.75;
        }
        return Math.max(0.5, s);
      };

      // Primer intento de export
      const doExport = async (scale) => {
        return await exportToBlob({
          elements,
          appState: { ...appState, exportBackground: true, viewBackgroundColor: "#ffffff" },
          files: allFiles,
          mimeType: "image/png",
          quality: 1,
          getDimensions: (w, h) => {
            const s = computeScale(w, h);
            return { width: w * s, height: h * s, scale: s };
          },
        });
      };

      let blob = await doExport(2);

      // Bug 2.8: Retry con scale reducido si el blob sale vacío
      if (!blob || blob.size < 100) {
        blob = await doExport(1);
      }
      if (!blob || blob.size < 100) {
        if (!silent) toast.error("No se pudo generar la imagen del pizarrón (canvas demasiado grande)");
        setExportingPdf(false);
        return;
      }

      // Convertir PNG → PDF con jsPDF (landscape o portrait según ratio)
      const url = URL.createObjectURL(blob);
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
      const isLandscape = img.width >= img.height;
      const pdf = new jsPDF({ orientation: isLandscape ? "landscape" : "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pageW / img.width, pageH / img.height);
      const drawW = img.width * ratio;
      const drawH = img.height * ratio;
      const x = (pageW - drawW) / 2;
      const y = (pageH - drawH) / 2;
      // Fondo blanco explícito para evitar artefactos de transparencia (C29).
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageW, pageH, "F");
      pdf.addImage(img, "PNG", x, y, drawW, drawH);
      const filename = `pizarron-${classId}-${new Date().toISOString().slice(0,10)}.pdf`;
      // Descarga local SOLO en export manual.
      if (!silent) pdf.save(filename);
      // Subida al backend
      const pdfBlob = pdf.output("blob");
      const fd = new FormData();
      fd.append("class_id", classId);
      if (recordingId) fd.append("recording_id", recordingId);
      fd.append("file", pdfBlob, filename);
      try {
        await api.post("/recordings/whiteboard-pdf", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,
        });
        toast.success(silent ? "Notas del pizarrón guardadas junto a la grabación" : "PDF descargado y vinculado a la grabación");
      } catch (e) {
        if (!silent) toast.info("PDF descargado localmente (no había grabación asociada)");
      }
      URL.revokeObjectURL(url);
    } catch (e) {
      if (!silent) toast.error("No se pudo exportar el PDF: " + (e?.message || "error"));
    } finally {
      setExportingPdf(false);
    }
  };

  // Exponemos exportPdf al contenedor (LiveClassRoom) para auto-guardar el
  // pizarrón en PDF cuando la grabación termina y se sube.
  useImperativeHandle(ref, () => ({
    exportPdf,
    broadcastShare: (shared) => sendControl("share_board", { shared: !!shared }),
    setStudentDrawing: (allowed) => sendControl("student_draw", { allowed: !!allowed }),
  }), [classId]);

  const initialData = useMemo(() => ({
    appState: { viewBackgroundColor: "#ffffff", currentItemFontFamily: 1, isCollaborating: true },
    elements: [],
    scrollToContent: true,
  }), []);

  return (
    <div className="relative w-full h-full bg-white rounded-xl overflow-hidden border-2 border-[#E1E5F0] flex flex-col" data-testid={`live-whiteboard-${classId}`}>
      {/* Barra de estado */}
      <div className="px-3 py-1.5 bg-[#F7F8FA] border-b border-[#E1E5F0] flex items-center gap-3 text-[11px] font-bold shrink-0">
        {status === "online" ? (
          <span className="text-[#3FA84D] inline-flex items-center gap-1" data-testid="wb-status-online">
            <Wifi size={12} /> En vivo
          </span>
        ) : status === "connecting" ? (
          <span className="text-[#9C7A18] inline-flex items-center gap-1" data-testid="wb-status-connecting">
            <Wifi size={12} className="animate-pulse" /> Conectando…
          </span>
        ) : (
          <span className="text-[#A52258] inline-flex items-center gap-1" data-testid="wb-status-offline">
            <WifiOff size={12} /> Sin conexión
          </span>
        )}
        <span className="text-[#6E7591] inline-flex items-center gap-1">
          <Users size={11} /> Pizarra colaborativa
        </span>
        {lastEditor && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold text-[#A43790] bg-[#FFE9F0] rounded-full px-2 py-0.5"
            data-testid="wb-last-editor"
            title="Última edición"
          >
            ✏️ {lastEditor}
          </span>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setShowMathEditor(true)}
          data-testid="wb-formula-toggle"
          disabled={insertingFormula}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded border-2 text-[10px] font-extrabold border-[#E1E5F0] text-[#A43790] hover:border-[#A43790] hover:bg-[#FFE9F0] disabled:opacity-50`}
          title="Editor de fórmulas: escribe LaTeX y previsualiza"
        >
          {insertingFormula ? <Loader2 size={11} className="animate-spin" /> : <Sigma size={11} />} Fórmulas
        </button>
        {canModerate && (
          <>
            <input
              ref={ocrInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onOcrFile}
              data-testid="wb-ocr-input"
            />
            <button
              type="button"
              onClick={() => ocrInputRef.current?.click()}
              disabled={ocrLoading}
              data-testid="wb-ocr-btn"
              className="inline-flex items-center gap-1 px-2 py-1 rounded border-2 border-[#E1E5F0] text-[#2F7C3A] hover:border-[#3FA84D] hover:bg-[#F4FBEF] text-[10px] font-extrabold disabled:opacity-40"
              title="Sube una foto manuscrita y conviértela en texto con IA"
            >
              {ocrLoading ? <Loader2 size={11} className="animate-spin" /> : <ImageUp size={11} />} {ocrLoading ? "Leyendo…" : "Foto → texto"}
            </button>
            <button
              type="button"
              onClick={() => setShowTableModal(true)}
              data-testid="wb-table-toggle"
              className="inline-flex items-center gap-1 px-2 py-1 rounded border-2 text-[10px] font-extrabold border-[#E1E5F0] text-[#3FA84D] hover:border-[#3FA84D] hover:bg-[#F0FBF2]"
              title="Insertar una tabla en la pizarra"
            >
              <TableIcon size={11} /> Tabla
            </button>
            <button
              type="button"
              onClick={() => { const next = !studentCanDraw; setStudentCanDraw(next); sendControl("student_draw", { allowed: next }); toast.info(next ? "El estudiante ya puede dibujar" : "Dibujo del estudiante bloqueado"); }}
              data-testid="wb-student-draw-toggle"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded border-2 text-[10px] font-extrabold ${studentCanDraw ? "border-[#2C49A0] bg-[#E8EEFB] text-[#2C49A0]" : "border-[#E1E5F0] text-[#6E7591] hover:border-[#2C49A0]"}`}
              title="Permitir o bloquear que el estudiante dibuje en la pizarra"
            >
              <Users size={11} /> Dibujo alumno: {studentCanDraw ? "ON" : "OFF"}
            </button>
          </>
        )}
        <button
          type="button"
          onClick={useHighlighter}
          data-testid="wb-highlighter"
          className="inline-flex items-center gap-1 px-2 py-1 rounded border-2 border-[#E1E5F0] text-[#9C7A18] hover:border-[#FFCC28] hover:bg-[#FFF7DA] text-[10px] font-extrabold"
          title="Resaltador: dibuja una caja amarilla translúcida sobre el contenido"
        >
          <Highlighter size={11} /> Resaltador
        </button>
        <button
          type="button"
          onClick={() => exportPdf()}
          disabled={exportingPdf}
          data-testid="wb-export-pdf"
          className="inline-flex items-center gap-1 px-2 py-1 rounded border-2 border-[#E1E5F0] text-[#2C49A0] hover:border-[#2C49A0] hover:bg-[#E8EEFB] text-[10px] font-extrabold disabled:opacity-40"
          title="Exportar el contenido del pizarrón a PDF y vincularlo a la grabación"
        >
          <FileDown size={11} /> {exportingPdf ? "Generando…" : "PDF"}
        </button>
        {canModerate && (
          <button
            type="button"
            onClick={clearBoard}
            data-testid="wb-clear"
            className="inline-flex items-center gap-1 px-2 py-1 rounded border-2 border-[#E1E5F0] text-[#A52258] hover:border-[#A52258] hover:bg-[#FFE9F0] text-[10px] font-extrabold"
            title="Limpiar pizarra (sólo docente)"
          >
            <Eraser size={11} /> Limpiar
          </button>
        )}
      </div>

      {showMathEditor && (
        <MathFormulaModal
          onClose={() => setShowMathEditor(false)}
          onConfirm={insertFormula}
        />
      )}

      {showTableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-80 space-y-4">
            <h3 className="font-bold text-[#1F2547]">Insertar tabla</h3>
            <div className="flex gap-3">
              <label className="flex-1 text-xs font-bold text-[#6E7591]">
                Filas
                <input type="number" min={1} max={20} value={tableRows}
                  onChange={(e) => setTableRows(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                  className="w-full mt-1 px-2 py-1.5 rounded border-2 border-[#E1E5F0]" />
              </label>
              <label className="flex-1 text-xs font-bold text-[#6E7591]">
                Columnas
                <input type="number" min={1} max={20} value={tableCols}
                  onChange={(e) => setTableCols(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                  className="w-full mt-1 px-2 py-1.5 rounded border-2 border-[#E1E5F0]" />
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowTableModal(false)} className="px-3 py-1.5 rounded text-sm font-bold text-[#6E7591]">Cancelar</button>
              <button onClick={insertTable} className="px-3 py-1.5 rounded bg-[#3FA84D] text-white text-sm font-bold">Insertar</button>
            </div>
          </div>
        </div>
      )}

      {/* Lienzo Excalidraw — solo renderiza post-mount (Fix 2.1.3) */}
      <div className="flex-1 min-h-0 relative" style={{ touchAction: "none" }}>
        {excalidrawReady && (
          <Excalidraw
            excalidrawAPI={(api) => setExcalidrawAPI(api)}
            initialData={initialData}
            onChange={onChange}
            onPointerUpdate={handlePointerUpdate}
            viewModeEnabled={!canModerate && !studentCanDraw}
            UIOptions={{
              canvasActions: {
                loadScene: false,
                saveAsImage: canModerate,
                export: false,
                clearCanvas: false,
                changeViewBackgroundColor: false,
                toggleTheme: false,
              },
              tools: {},
            }}
            langCode="es-ES"
          />
        )}
      </div>
    </div>
  );
});

export default LiveWhiteboard;
